import {Injectable} from '@angular/core';
import {User} from '@spartacus/core';
import {LATIN_1_WITH_ASCII_REGEX} from '../dcp-validators';
import {ForbiddenWordService} from './forbidden-word.service';
import {LocalStorageService} from '@dcp/configurator';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

const PREFIX = 'registration';
export const REGISTRATION_STORAGE_FIRST_NAME = PREFIX + 'FirstName';
export const REGISTRATION_STORAGE_LAST_NAME = PREFIX + 'LastName';

export interface UserNameState {
  firstName: string;
  lastName: string;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class UserNameValidationService {

  private user: User;

  constructor(
    private forbiddenWordService: ForbiddenWordService,
    private userAccountFacade: UserAccountFacade,
    private localStorageService: LocalStorageService,
  ) {
    this.userAccountFacade.get().pipe(untilDestroyed(this)).subscribe((user) => {
      this.user = user;
    });
  }

  public isUserNameNonLatin(user: User) {
    if (user) {
      const regexToTest = new RegExp(LATIN_1_WITH_ASCII_REGEX);
      return regexToTest.test(user.firstName) || regexToTest.test(user.lastName);
    }
    return false;
  }

  public isUserNameTooLong(user: User) {
    return user?.firstName?.length > 40 || user?.lastName?.length > 40;
  }

  public isUserNameForbidden(user: User): boolean {
    return this.forbiddenWordService.checkForbiddenWords(user.firstName) ||
      this.forbiddenWordService.checkForbiddenWords(user.lastName);
  }

  public isFirstNameSameAsLastName(user: User): boolean {
    return user?.firstName && user?.lastName && user.firstName.trim().toLowerCase() === user.lastName.trim().toLowerCase();
  }

  public isUserNameValid(user: User): boolean {
    return !!user &&
      (!this.isUserNameNonLatin(user) || this.isSupplier(user)) &&
      !this.isUserNameTooLong(user) &&
      !this.isUserNameForbidden(user) &&
      !this.isFirstNameSameAsLastName(user);
  }

  public isSupplier(user: User): boolean {
    return user.scimUserId?.toUpperCase().startsWith('AN');
  }

  public setValidUserName(firstName: string, lastName: string) {
    this.localStorageService.setItem(REGISTRATION_STORAGE_FIRST_NAME, firstName);
    this.localStorageService.setItem(REGISTRATION_STORAGE_LAST_NAME, lastName);
  }

  public getCorrectedUserName(): UserNameState | undefined {
    const firstName = this.localStorageService.getItem(REGISTRATION_STORAGE_FIRST_NAME);
    const lastName = this.localStorageService.getItem(REGISTRATION_STORAGE_LAST_NAME);
    if (firstName && lastName) {
      return {firstName, lastName};
    }
    return undefined;
  }

  public getValidUserName(): UserNameState | undefined {
    if (this.isUserNameValid(this.user)) {
      return {firstName: this.user.firstName, lastName: this.user.lastName};
    }
    return this.getCorrectedUserName();
  }

  public reset() {
    this.localStorageService.removeLocalStorageItemsByPrefix(PREFIX);
  }
}
